import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, EnvironmentProviders, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

import { AppRoutingModule } from './app-routing.module';
import { LanguageService } from './core/language.service';
import { CommonModule } from '@angular/common';
import { AppErrorHandler } from './app-error-handler';
import { AppInitComponent } from './app-init/app-init.component';
import { AppComponentModule } from './app-component.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { MatDialogModule } from '@angular/material/dialog';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';

import mapGoogleAnalyticRouter from './core/mapGoogleAnalyticRouter';
import { NgxGoogleAnalyticsModule } from './ngx-google-analytics/ngx-google-analytics.module';
import { XnLazymoduleModule } from './xn-lazymodule/xn-lazymodule.module';
import { XN_LAZY_MODULES } from './xn-lazy-modules-register';
import { ErrorInterceptor } from './core/error.interceptor';
import * as Sentry from "@sentry/angular";
import { Router } from '@angular/router';
import Aura from '@primeuix/themes/aura';

const gaData = mapGoogleAnalyticRouter(window.location.pathname);

// Import the module from the SDK
import { AuthModule } from '@auth0/auth0-angular';
import { providePrimeNG } from 'primeng/config';

// read from config
function initializeKeycloak(keycloak: KeycloakService,) {
  if(!(environment as any).keyCloakConfig) return () => {};

  return () =>
    keycloak.init({
      config: {
        url: (environment as any).keyCloakConfig?.url || '',
        realm: (environment as any).keyCloakConfig?.realm || '',
        clientId: (environment as any).keyCloakConfig?.clientId || ''
      },
      initOptions: {
        checkLoginIframe: false
      },
      enableBearerInterceptor: false
    });
}

function getOktaRedirectURL(): string{
  let tag = document.querySelector('base');
  if(tag){
    let url = tag.href;
    if(url !== '' && url !== '/'){
      return url;
    }
  }
  return window.location.origin;
}

const importModules = [
  CoreModule,
  MatDialogModule,
  CommonModule,
  HttpClientModule,
  BrowserModule,
  BrowserAnimationsModule,
  KeycloakAngularModule,
  // Routers
  AppRoutingModule,

  NgIdleKeepaliveModule.forRoot(),
  AppComponentModule,

  // google analytic
  NgxGoogleAnalyticsModule.forRoot(environment.googleTagManagerId, undefined, undefined, undefined, undefined, true),
  
  // register xn lazy modules
  XnLazymoduleModule.forRoot(XN_LAZY_MODULES),
];

if(environment.externalAuthorizationProvider === 'Okta' || true){
  // Always must import Okta module to prevent error even do not use Okta
  // Import the module into the application, with configuration
  importModules.push(AuthModule.forRoot({
    domain: environment.oktaConfig ? environment.oktaConfig.domain : '',
    clientId: environment.oktaConfig ? environment.oktaConfig.clientId: '',
    authorizationParams: {
      redirect_uri: getOktaRedirectURL()
    }
  }))
}

const providers: Array<Provider | EnvironmentProviders> = [
  { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  { provide: ErrorHandler, useClass: AppErrorHandler },
  providePrimeNG({
    theme: {
      preset: Aura,
      options: {
        // disable dark mode entirely
        darkModeSelector: false
      }
    }
  }),
];

if(environment.externalAuthorizationProvider === 'Keycloak'){
  providers.push({
    provide: APP_INITIALIZER,
    useFactory: initializeKeycloak,
    multi: true,
    deps: [KeycloakService]
  });
}

if(environment.turnOnSentry && environment.sentryDSN){
  providers.push({
    provide: Sentry.TraceService,
    deps: [Router],
  });
}

@NgModule({
  declarations: [
    AppInitComponent,
  ],
  imports: importModules,
  providers: providers,
  bootstrap: [
    AppComponent,
  ]
})
export class AppModule {
  constructor(
    private language: LanguageService,
    private trace: Sentry.TraceService
  ) {    
    this.language.init();
  }
 }
